import { TabsTrigger } from '@radix-ui/react-tabs'
import {
  type RowSelectionState,
  type SortingState,
  type VisibilityState,
} from '@tanstack/react-table'
import clsx from 'clsx'
import { ChevronDown } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useLocalStorage } from 'usehooks-ts'

import BrokerContext from 'components/context/BrokerContext'
import NeedsAttentionContext from 'components/context/NeedsAttentionContext'
import {
  getBrokersMissingPaymentsQueryKey,
  type NAVv2Filters,
} from 'components/hooks/useGetBrokersMissingPayments'
import AnimateOpacity from 'components/reusable/AnimateOpacity'
import RowSelectionCountDisplay from 'components/reusable/table/RowSelectionCountDisplay'
import { Button } from 'components/ui/button'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from 'components/ui/dropdown-menu'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select'
import { Tabs, TabsList } from 'components/ui/tabs'

import { type Broker } from '../types'
import BulkUpdateItemsDialog from './BulkUpdateItemsDialog'
import CreateOrEditNAVItem, { type CreateOrEditNAVOptionsState } from './CreateOrEditNAVItem'
import NeedsAttentionCSVDownload from './NeedsAttentionCSVDownload'
import NeedsAttentionFilters from './NeedsAttentionFilters'
import NeedsAttentionStatistics from './NeedsAttentionStatistics'
import NeedsAttentionSyncFiltersWithURL from './NeedsAttentionSyncFiltersWithUrl'
import {
  MAX_NUMBER_OF_SELECTED_ITEMS,
  type NeedsAttentionPagination,
  type NeedsAttentionSettings,
  type NeedsAttentionSorting,
  NeedsAttentionTableImpl,
} from './NeedsAttentionTable'
import TagManagementSheet from './TagManagementSheet'

const DEFAULT_PAGE_SIZE = 20

function getInitialSorting (sort?: NeedsAttentionSorting) {
  if (sort?.state) {
    return [
      {
        id: 'status', // Tanstack is expecting the column header ID to be 'status'
        desc: sort.state === 'desc',
      },
    ]
  }
  if (sort?.created_at) {
    return [
      {
        id: 'created_at',
        desc: sort.created_at === 'desc',
      },
    ]
  }
  return []
}

const columnVisibilityMapping = {
  amount_paid: 'Amount Paid',
  employee: 'Employee',
  enrollment_type: 'Enrollment Type',
  employer_name: 'Employer',
  carrier_name: 'Carrier Name',
  brokerage_name: 'Brokerage',
  submission_method: 'Submission Method',
  category: 'Category',
  state: 'State',
  amount_expected: 'Amount Expected',
  notes: 'Notes',
  silenced_by_name: 'Silenced By',
}

const defaultColumnVisibility = {
  amount_paid: true,
  employee: true,
  enrollment_type: true,
  employer_name: true,
  carrier_name: true,
  brokerage_name: true,
  submission_method: true,
  category: true,
  state: true,
  amount_expected: true,
  notes: true,
  silenced_by_name: true,
}

export type NeedsAttentionViewProps = {
  pageParam?: number | null
  sortParams?: NeedsAttentionSorting
  currentBroker?: Broker
  isNAVAdmin?: boolean
  isNAVv2ToolingEnabled?: boolean
  isNAVv2EndpointEnabled?: boolean
}

export const NeedsAttentionViewImpl = (props: NeedsAttentionViewProps) => {
  const {
    isNAVAdmin,
    isNAVv2ToolingEnabled,
    currentBroker,
    sortParams,
    pageParam,
    isNAVv2EndpointEnabled,
  } = props
  const [filters, setV2Filters] = useState<NAVv2Filters>({})

  const [isActionsMenuOpen, setIsActionsMenuOpen] = useState(false)
  const [isTagManagementOpen, setIsTagManagementOpen] = useState(false)
  const [isBulkUpdateOpen, setIsBulkUpdateOpen] = useState(false)
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})
  const [activeTab, setActiveTab] = useState<'triage' | 'statistics'>('triage')

  const initialSorting = getInitialSorting(sortParams)

  const [sorting, setSorting] = useState<SortingState>(initialSorting)
  const [settings, setSettings] = useLocalStorage<NeedsAttentionSettings>(
    'needs-attention-settings',
    {
      pageSize: DEFAULT_PAGE_SIZE.toString(),
    }
  )

  const [columnVisibilitySettings, setColumnVisibilitySettings] =
    useLocalStorage<VisibilityState>(
      'needs-attention-column-visibility-settings',
      defaultColumnVisibility
    )

  const [pagination, setPagination] = useState<NeedsAttentionPagination>({
    pageIndex: pageParam || 1,
    pageSize: parseInt(settings.pageSize),
  })

  const [createOrEditNAVItemOptions, setCreateOrEditNAVItemOptions] =
      useState<CreateOrEditNAVOptionsState>({
        isOpen: false,
        item: undefined,
      })

  const [columnVisibility, setColumnVisibility] = useState(
    {
      ...columnVisibilitySettings,
      // Only show newer fields if they've been rolled out via the feature flag.
      ...(isNAVv2ToolingEnabled
        ? {}
        : {
            select: false,
            submission_method: false,
            tags: false,
            brokers: false,
            category: false,
            state: false,
          })

    }
  )

  const missingPaymentsArgs = {
    pagination,
    sorting,
    filters,
    isV2Enabled: isNAVv2EndpointEnabled,
  }

  // Reset row selection when filters and/or sorting change. This is to prevent the user from bulk updating items no
  // longer visible on the page.
  useEffect(() => {
    setRowSelection({})
  }, [pagination.pageSize, sorting, JSON.stringify(filters)])

  const handlePageSizeChange = (value: string) => {
    setPagination({ pageIndex: 1, pageSize: parseInt(value) })
    setSettings({ pageSize: value })
  }

  useEffect(() => {
    setColumnVisibilitySettings(columnVisibility)
  }, [columnVisibility])

  const numberOfSelectedItems =
    Object.values(rowSelection).filter(Boolean).length

  return (
    <NeedsAttentionContext.Provider
      value={{
        isNAVAdmin,
        isNAVv2ToolingEnabled,
        getBrokersMissingPaymentsQueryKey:
          getBrokersMissingPaymentsQueryKey(missingPaymentsArgs),
        createOrEditNAVItemOptions,
        setCreateOrEditNAVItemOptions,
      }}
    >
      <BrokerContext.Provider value={{ currentBroker }}>
        <CreateOrEditNAVItem
          isOpen={createOrEditNAVItemOptions.isOpen}
          item={createOrEditNAVItemOptions.item}
          setIsOpen={setCreateOrEditNAVItemOptions}
        />
        <NeedsAttentionSyncFiltersWithURL
          filters={filters}
          sorting={sorting}
          pagination={pagination}
          setV2Filters={setV2Filters}
          isNAVv2ToolingEnabled={isNAVv2ToolingEnabled || false}
        />
        <TagManagementSheet
          isOpen={isTagManagementOpen}
          setIsOpen={setIsTagManagementOpen}
        />
        <BulkUpdateItemsDialog
          isOpen={isBulkUpdateOpen}
          setIsOpen={setIsBulkUpdateOpen}
          selectedItems={Object.entries(rowSelection).reduce<string[]>(
            (acc, [key, value]) => (value ? [...acc, key] : acc),
            []
          )}
        />
        <div className='flex items-center justify-between w-full mb-4 gap-x-2'>
          <div className='text-2xl font-semibold w-96'>Needs Attention</div>
          {isNAVv2ToolingEnabled && (
            <div className='flex items-center justify-end w-full gap-x-2'>
              <Button variant='secondary' onClick={() => setV2Filters({})}>
                Clear Filters
              </Button>

              <DropdownMenu
                open={isActionsMenuOpen}
                onOpenChange={setIsActionsMenuOpen}
              >
                <DropdownMenuTrigger asChild>
                  <Button variant='outline' data-testid='actions-menu'>
                    Actions
                    <ChevronDown className='ml-1' size={12} />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuItem onClick={() => setIsTagManagementOpen(true)}>
                    Manage Tags
                  </DropdownMenuItem>
                  <DropdownMenuItem>
                    <NeedsAttentionCSVDownload
                      setIsActionsMenuOpen={setIsActionsMenuOpen}
                      filters={filters}
                    />
                  </DropdownMenuItem>
                  <DropdownMenuSub>
                    <DropdownMenuSubTrigger>Columns</DropdownMenuSubTrigger>
                    <DropdownMenuPortal>
                      <DropdownMenuSubContent>
                        {Object.entries(columnVisibilityMapping).map(
                          ([key, label]) => {
                            return (
                              <DropdownMenuCheckboxItem
                                key={key}
                                checked={
                                  columnVisibility[
                                    key as keyof typeof columnVisibility
                                  ]
                                }
                                onCheckedChange={(checked) => {
                                  setColumnVisibility((prev) => ({
                                    ...prev,
                                    [key]: checked,
                                  }))
                                }}
                              >
                                {label}
                              </DropdownMenuCheckboxItem>
                            )
                          }
                        )}
                      </DropdownMenuSubContent>
                    </DropdownMenuPortal>
                  </DropdownMenuSub>
                </DropdownMenuContent>
              </DropdownMenu>

              <Button
                disabled={numberOfSelectedItems === 0}
                onClick={() => setIsBulkUpdateOpen(true)}
              >
                Update Selected
              </Button>
            </div>
          )}
        </div>

        {/* View Tabs */}
        <Tabs
          onValueChange={(value) =>
            setActiveTab(value as 'triage' | 'statistics')}
        >
          <TabsList className='flex items-center justify-between w-full mb-4'>
            {['triage', 'statistics'].map((value) => (
              <TabsTrigger
                key={value}
                value={value}
                className={clsx({
                  'w-[49.5%] rounded-md py-1': true,
                  'bg-purple-500 text-white font-semibold': activeTab === value,
                  'bg-white text-gray-500 font-light': activeTab !== value,
                })}
              >
                {value === 'triage' ? 'Triage' : 'Statistics'}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        <NeedsAttentionFilters
          filters={filters}
          setFilters={setV2Filters}
          pagination={pagination}
          setPagination={setPagination}
        />

        {activeTab === 'triage' && (
          <AnimateOpacity key={activeTab} isOpen>
            <div className='h-10 mb-2 flex items-center justify-between w-full'>
              <RowSelectionCountDisplay
                numberOfSelectedItems={numberOfSelectedItems}
                setRowSelection={setRowSelection}
                maxNumberOfSelectedItems={MAX_NUMBER_OF_SELECTED_ITEMS}
              />
              <div className='max-w-[50%] flex items-center justify-between space-x-2'>
                <div className='font-light text-sm text-nowrap'>
                  Records per page
                </div>
                <div className='w-[100px]'>
                  <Select
                    value={pagination.pageSize.toString()}
                    onValueChange={handlePageSizeChange}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder='Page size' />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value='10'>10</SelectItem>
                      <SelectItem value='20'>20</SelectItem>
                      <SelectItem value='50'>50</SelectItem>
                      <SelectItem value='100'>100</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <NeedsAttentionTableImpl
              {...props}
              missingPaymentsArgs={missingPaymentsArgs}
              setSorting={setSorting}
              setSettings={setSettings}
              setPagination={setPagination}
              rowSelection={rowSelection}
              setRowSelection={setRowSelection}
              columnVisibilityOverrides={columnVisibility}
            />
          </AnimateOpacity>
        )}

        {activeTab === 'statistics' && (
          <AnimateOpacity key={activeTab} isOpen>
            <NeedsAttentionStatistics filters={filters} />
          </AnimateOpacity>
        )}
      </BrokerContext.Provider>
    </NeedsAttentionContext.Provider>
  )
}

const NeedsAttentionView = (props: NeedsAttentionViewProps) => {
  return <NeedsAttentionViewImpl {...props} />
}

export default NeedsAttentionView
